import { QrCodeImage } from '@/components/hotel-access-codes/QrCodeImage';
import type { HotelAccessCode } from '@/config/objects/hotel-access-codes';

interface HotelAccessCodeQrCellProps {
  accessCode?: HotelAccessCode;
  size?: 'sm' | 'lg';
}

// Rendered as a <canvas> (a real raster image) rather than an <svg> so the
// browser's context menu offers "Save image as…" — guests/staff can right-click
// to download, or just scan it off the screen. Sizes are large enough to scan.
const PIXEL_SIZE: Record<NonNullable<HotelAccessCodeQrCellProps['size']>, number> = {
  // Was 72, which is small to scan off a screen and a poor click target
  // now that the cell navigates like the rest of the row.
  sm: 104,
  lg: 120,
};

/**
 * Generates and displays a scannable, downloadable QR code for an access code.
 *
 * @param accessCode - The access code whose `code` is encoded into the scan URL.
 * @param size - `sm` for list cells (default), `lg` for larger contexts.
 * @returns The QR code image, or a muted placeholder when no code is available.
 */
export function HotelAccessCodeQrCell({
  accessCode,
  size = 'sm',
}: HotelAccessCodeQrCellProps) {
  const pixelSize = PIXEL_SIZE[size];
  const boxClass = size === 'sm' ? 'h-[104px] w-[104px]' : 'h-[120px] w-[120px]';

  if (!accessCode?.code) {
    return (
      <div className={`${boxClass} rounded bg-muted flex items-center justify-center`}>
        <span className="text-xs text-muted-foreground">—</span>
      </div>
    );
  }

  // Mirror the URL format used by the detail page's QR card.
  const qrCodeUrl = `${window.location.origin}/api/v1/scans/${accessCode.code}`;

  return (
    <div className={`${boxClass} rounded bg-white p-1 flex items-center justify-center`}>
      <QrCodeImage
        value={qrCodeUrl}
        size={pixelSize - 8}
        alt={`QR code for ${accessCode.name ?? 'access code'}`}
      />
    </div>
  );
}
